import H from '@here/maps-api-for-javascript'
import { useTheme } from '@mui/material/styles'
import React from 'react'
import {
  buildHereTrafficTileUrl,
  fetchHereRoute,
  getDefaultCenter,
  HereRouteSummary,
  LatLngLike,
  LatLngLiteral,
  metersToMiles,
  normaliseCoordinate,
  prefetchHereVectorTiles,
} from './hereMaps'

type MarkerKind =
  | 'origin'
  | 'destination'
  | 'stop'
  | 'tracking'
  | 'trackingDelayed'
  | 'trackingWaiting'
  | 'trackingOnBoard'
  | 'trackingOnBoardDelayed'
  | 'person'
  | 'flag'
  | 'dot'
  | 'price'

export type HereMarker = {
  key?: string
  position: LatLngLike
  kind?: MarkerKind
  label?: string | number
  tooltip?: string
  zIndexOffset?: number
  onClick?: () => void
}

export type HereCircle = {
  key?: string
  position: LatLngLike
  radius?: number
  strokeColor?: string
  strokeOpacity?: number
  strokeWeight?: number
  fillColor?: string
  fillOpacity?: number
  onClick?: () => void
}

type HereNativeRouteMapProps = {
  origin?: LatLngLike
  destination?: LatLngLike
  additionalStops?: LatLngLike[]
  markers?: HereMarker[]
  circles?: HereCircle[]
  mapWidth?: string | number
  mapHeight?: string | number
  marginLeft?: string | number
  marginRight?: string | number
  borderRadius?: string | number
  boxShadow?: string
  defaultCenter?: LatLngLiteral
  defaultZoom?: number
  trimRouteFrom?: LatLngLike
  panTarget?: LatLngLike
  panZoom?: number
  panAnimated?: boolean
  extraRoutes?: Array<{
    key: string
    origin: LatLngLike
    destination: LatLngLike
    color?: string
  }>
  fitToPoints?: LatLngLike[]
  htmlOverlays?: Array<{
    key: string
    position: LatLngLike
    size: { width: number; height: number }
    tailHeight?: number
    preferAwayFrom?: LatLngLike
    render: (layout: {
      tailSide: 'top' | 'right' | 'bottom' | 'left'
      tailAttach: number
    }) => React.ReactNode
  }>
  viewportPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
  showRoute?: boolean
  showRouteMarkers?: boolean
  hideOriginMarker?: boolean
  allowFitBounds?: boolean
  animateRoute?: boolean
  animateRouteDurationMs?: number
  showTrafficOverlay?: boolean
  showMapTypeControl?: boolean
  mapTypeControlPosition?:
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
  mapOptions?: {
    zoomControl?: boolean
    scrollWheelZoom?: boolean
    dragging?: boolean
    doubleClickZoom?: boolean
    touchZoom?: boolean
    zoomLevel?: number
  }
  updateRouteInfo?: (info: {
    estimatedJourneyTime: number
    estimatedJourneyDistance: number
    directionsResponse: any
  }) => void
  onBoundsChange?: (bounds: {
    north: number
    south: number
    east: number
    west: number
  }) => void
  flightArc?: {
    from: LatLngLike
    to: LatLngLike
    color?: string
  }
  flightArcFlyoverKey?: number
  prefetchFlyoverTiles?: boolean
}

type MarkerStyle = {
  background: string
  border: string
  color: string
  fallbackLabel: string
}

const KIND_STYLES: Record<MarkerKind, MarkerStyle> = {
  origin: {
    background: '#0b7a32',
    border: '#085924',
    color: '#ffffff',
    fallbackLabel: 'A',
  },
  destination: {
    background: '#3b51f6',
    border: '#0943a1',
    color: '#ffffff',
    fallbackLabel: 'B',
  },
  stop: {
    background: '#f0c419',
    border: '#d5a900',
    color: '#12212b',
    fallbackLabel: 'S',
  },
  tracking: {
    background: '#00253d',
    border: '#001b2b',
    color: '#ffffff',
    fallbackLabel: '',
  },
  trackingDelayed: {
    background: '#00253d',
    border: '#c62828',
    color: '#ffffff',
    fallbackLabel: '',
  },
  trackingWaiting: {
    background: '#00253d',
    border: '#ef8a17',
    color: '#ffffff',
    fallbackLabel: '',
  },
  trackingOnBoard: {
    background: '#0b7a32',
    border: '#085924',
    color: '#ffffff',
    fallbackLabel: '',
  },
  trackingOnBoardDelayed: {
    background: '#0b7a32',
    border: '#c62828',
    color: '#ffffff',
    fallbackLabel: '',
  },
  person: {
    background: '#b6541b',
    border: '#8f4113',
    color: '#ffffff',
    fallbackLabel: 'P',
  },
  flag: {
    background: '#5c1d91',
    border: '#46156f',
    color: '#ffffff',
    fallbackLabel: 'F',
  },
  dot: {
    background: '#0d6efd',
    border: '#0a58ca',
    color: '#ffffff',
    fallbackLabel: '•',
  },
  price: {
    background: '#00253d',
    border: '#ffffff',
    color: '#ffffff',
    fallbackLabel: '£',
  },
}

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;'
      case '<':
        return '&lt;'
      case '>':
        return '&gt;'
      case '"':
        return '&quot;'
      case "'":
        return '&#39;'
      default:
        return c
    }
  })

const TRACKING_KINDS: ReadonlySet<MarkerKind> = new Set([
  'tracking',
  'trackingDelayed',
  'trackingWaiting',
  'trackingOnBoard',
  'trackingOnBoardDelayed',
])

const CAR_ICON_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11h.5a1.5 1.5 0 0 1 1.5 1.5V17a1 1 0 0 1-1 1h-1a1.5 1.5 0 0 1-1.5-1.5V16H6.5v.5A1.5 1.5 0 0 1 5 18H4a1 1 0 0 1-1-1v-4.5A1.5 1.5 0 0 1 4.5 11H5zm2.1 0h9.8l-1-3a.5.5 0 0 0-.5-.4H8.6a.5.5 0 0 0-.5.4l-1 3zM7 14a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm10 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
  </svg>
`

const buildMarkerHtml = (kind: MarkerKind, label?: string | number): string => {
  const style = KIND_STYLES[kind]
  const display = label ?? style.fallbackLabel
  if (kind === 'price') {
    const text = escapeHtml(String(display))
    return `
      <div style="
        display:inline-flex;align-items:center;justify-content:center;
        padding:6px 10px;border-radius:999px;
        background:${style.background};
        border:2px solid ${style.border};
        color:${style.color};
        font-size:12px;font-weight:700;font-family:system-ui,sans-serif;
        white-space:nowrap;line-height:1;
        box-shadow:0 2px 8px rgba(0,0,0,0.28);
        cursor:pointer;
      ">${text}</div>
    `
  }
  const content =
    label == null && TRACKING_KINDS.has(kind) ? CAR_ICON_SVG : display
  return `
    <div style="
      width:30px;height:30px;border-radius:999px;
      background:${style.background};
      border:2px solid ${style.border};
      color:${style.color};
      display:flex;align-items:center;justify-content:center;
      font-size:13px;font-weight:700;font-family:system-ui,sans-serif;
      box-shadow:0 2px 8px rgba(0,0,0,0.28);
    ">${content}</div>
  `
}

const iconCache = new Map<string, H.map.DomIcon>()

const getKindIcon = (
  kind: MarkerKind,
  label?: string | number,
): H.map.DomIcon => {
  const key = `${kind}|${label ?? ''}`
  let icon = iconCache.get(key)
  if (!icon) {
    icon = new H.map.DomIcon(buildMarkerHtml(kind, label), {
      onAttach: (clonedElement) => {
        const el = clonedElement as HTMLElement
        if (kind === 'price') {
          const rect = el.getBoundingClientRect?.()
          const w = rect?.width || el.offsetWidth || 60
          const h = rect?.height || el.offsetHeight || 24
          el.style.transform = `translate(${-w / 2}px, ${-h / 2}px)`
        } else {
          el.style.transform = 'translate(-15px, -15px)'
        }
      },
    })
    iconCache.set(key, icon)
  }
  return icon
}

const projectPointOnSegment = (
  point: LatLngLiteral,
  from: LatLngLiteral,
  to: LatLngLiteral,
): { point: LatLngLiteral; distanceSquared: number } => {
  const dx = to.lng - from.lng
  const dy = to.lat - from.lat
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) {
    return {
      point: from,
      distanceSquared:
        (point.lng - from.lng) * (point.lng - from.lng) +
        (point.lat - from.lat) * (point.lat - from.lat),
    }
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.lng - from.lng) * dx + (point.lat - from.lat) * dy) /
        lengthSquared,
    ),
  )
  const projected = { lat: from.lat + dy * t, lng: from.lng + dx * t }
  return {
    point: projected,
    distanceSquared:
      (point.lng - projected.lng) * (point.lng - projected.lng) +
      (point.lat - projected.lat) * (point.lat - projected.lat),
  }
}

const trimPolylinesFromPoint = (
  polylines: LatLngLiteral[][],
  trimPoint: LatLngLiteral | null,
): LatLngLiteral[][] => {
  if (!trimPoint || !polylines.length) return polylines
  let best: {
    sectionIndex: number
    segmentIndex: number
    projected: LatLngLiteral
    distanceSquared: number
  } | null = null
  polylines.forEach((section, sectionIndex) => {
    if (section.length === 0) return
    if (section.length === 1) {
      const only = section[0]
      const d2 =
        (trimPoint.lng - only.lng) * (trimPoint.lng - only.lng) +
        (trimPoint.lat - only.lat) * (trimPoint.lat - only.lat)
      if (!best || d2 < best.distanceSquared) {
        best = {
          sectionIndex,
          segmentIndex: 0,
          projected: only,
          distanceSquared: d2,
        }
      }
      return
    }
    for (let i = 1; i < section.length; i++) {
      const cand = projectPointOnSegment(trimPoint, section[i - 1], section[i])
      if (!best || cand.distanceSquared < best.distanceSquared) {
        best = {
          sectionIndex,
          segmentIndex: i - 1,
          projected: cand.point,
          distanceSquared: cand.distanceSquared,
        }
      }
    }
  })
  if (!best) return polylines
  const bestNonNull = best as {
    sectionIndex: number
    segmentIndex: number
    projected: LatLngLiteral
    distanceSquared: number
  }
  const trimmed = polylines
    .slice(bestNonNull.sectionIndex)
    .map((section, relIdx) => {
      if (relIdx !== 0) return section
      if (section.length <= 1) return section
      const prefix = section.slice(bestNonNull.segmentIndex + 1)
      const first = prefix[0]
      if (
        first &&
        first.lat === bestNonNull.projected.lat &&
        first.lng === bestNonNull.projected.lng
      ) {
        return prefix
      }
      return [bestNonNull.projected, ...prefix]
    })
    .filter((s) => s.length > 0)
  return trimmed.length ? trimmed : polylines
}

const getPolylineLength = (points: LatLngLiteral[]): number => {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]
    const b = points[i]
    const dx = b.lng - a.lng
    const dy = b.lat - a.lat
    total += Math.sqrt(dx * dx + dy * dy)
  }
  return total
}

const clipPolylineToDistance = (
  points: LatLngLiteral[],
  distance: number,
): LatLngLiteral[] => {
  if (points.length < 2 || distance <= 0) return points.slice(0, 1)
  const result: LatLngLiteral[] = [points[0]]
  let remaining = distance
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]
    const b = points[i]
    const dx = b.lng - a.lng
    const dy = b.lat - a.lat
    const segLen = Math.sqrt(dx * dx + dy * dy)
    if (segLen === 0) continue
    if (remaining >= segLen) {
      result.push(b)
      remaining -= segLen
    } else {
      const t = remaining / segLen
      result.push({ lat: a.lat + dy * t, lng: a.lng + dx * t })
      return result
    }
  }
  return result
}

const FALLBACK_ZOOM = 10
const ROUTE_COLOR_DEFAULT = '#0b1c33'
const ROUTE_WIDTH_DEFAULT = 5
const ARC_COLOR_DEFAULT = '#002EA5'

const sampleBezier = (
  from: LatLngLiteral,
  to: LatLngLiteral,
  samples: number,
  bow = 0.22,
): LatLngLiteral[] => {
  const dx = to.lng - from.lng
  const dy = to.lat - from.lat
  const midLat = (from.lat + to.lat) / 2
  const midLng = (from.lng + to.lng) / 2
  const ctrlLat = midLat + -dx * bow
  const ctrlLng = midLng + dy * bow
  const points: LatLngLiteral[] = []
  for (let i = 0; i <= samples; i++) {
    const t = i / samples
    const oneMinusT = 1 - t
    points.push({
      lat:
        oneMinusT * oneMinusT * from.lat +
        2 * oneMinusT * t * ctrlLat +
        t * t * to.lat,
      lng:
        oneMinusT * oneMinusT * from.lng +
        2 * oneMinusT * t * ctrlLng +
        t * t * to.lng,
    })
  }
  return points
}

const toLineString = (points: LatLngLiteral[]): H.geo.LineString => {
  const ls = new H.geo.LineString()
  for (const p of points)
    ls.pushPoint({ lat: p.lat, lng: p.lng } as H.geo.IPoint)
  return ls
}

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const haversineKm = (a: LatLngLiteral, b: LatLngLiteral): number => {
  const R = 6371
  const φ1 = (a.lat * Math.PI) / 180
  const φ2 = (b.lat * Math.PI) / 180
  const dφ = ((b.lat - a.lat) * Math.PI) / 180
  const dλ = ((b.lng - a.lng) * Math.PI) / 180
  const s =
    Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

const computeCruiseZoom = (distanceKm: number, closeZoom = 11): number => {
  const REF_KM = 300
  const LONG_KM = 15000
  const MAX_ZOOM_OUT = 8
  const safe = Math.max(distanceKm, REF_KM)
  const t = Math.min(
    1,
    Math.log10(safe / REF_KM) / Math.log10(LONG_KM / REF_KM),
  )
  return Math.max(2, closeZoom - MAX_ZOOM_OUT * t)
}

const computeBearing = (a: LatLngLiteral, b: LatLngLiteral): number => {
  const φ1 = (a.lat * Math.PI) / 180
  const φ2 = (b.lat * Math.PI) / 180
  const Δλ = ((b.lng - a.lng) * Math.PI) / 180
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360
}

const AIRPLANE_PATH =
  'M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z'

const buildPlaneOverlay = (color: string): HTMLDivElement => {
  const el = document.createElement('div')
  el.style.position = 'absolute'
  el.style.left = '0'
  el.style.top = '0'
  el.style.width = '36px'
  el.style.height = '36px'
  el.style.marginLeft = '-18px'
  el.style.marginTop = '-18px'
  el.style.pointerEvents = 'none'
  el.style.willChange = 'transform'
  el.style.transformOrigin = 'center'
  el.style.zIndex = '500'
  el.innerHTML = `<svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="display:block"><path fill="${color}" d="${AIRPLANE_PATH}" /></svg>`
  return el
}

const buildShadowOverlay = (): HTMLDivElement => {
  const el = document.createElement('div')
  el.style.position = 'absolute'
  el.style.left = '0'
  el.style.top = '0'
  el.style.width = '36px'
  el.style.height = '36px'
  el.style.marginLeft = '-18px'
  el.style.marginTop = '-18px'
  el.style.pointerEvents = 'none'
  el.style.willChange = 'transform'
  el.style.transformOrigin = 'center'
  el.style.opacity = '0.45'
  el.style.filter = 'blur(1.5px)'
  el.style.zIndex = '499'
  el.innerHTML = `<svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="display:block"><path fill="#000000" d="${AIRPLANE_PATH}" /></svg>`
  return el
}

const PERF_ENABLED =
  process.env.NODE_ENV !== 'production' &&
  typeof performance !== 'undefined' &&
  typeof performance.mark === 'function'

const perfEnabled = (): boolean => {
  if (!PERF_ENABLED) return false
  try {
    return window.localStorage.getItem('hereMap:perf') !== 'off'
  } catch {
    return true
  }
}

const perfMark = (name: string) => {
  if (!perfEnabled()) return
  try {
    performance.mark(name)
  } catch {
    /* swallow */
  }
}

const perfMeasure = (name: string, start: string, end?: string) => {
  if (!perfEnabled()) return
  try {
    if (end) performance.measure(name, start, end)
    else performance.measure(name, start)
  } catch {
    /* swallow */
  }
}

function HereNativeRouteMap(props: HereNativeRouteMapProps) {
  const {
    origin,
    destination,
    additionalStops = [],
    markers = [],
    circles = [],
    mapWidth = '100%',
    mapHeight = '250px',
    marginLeft,
    marginRight,
    borderRadius,
    boxShadow,
    defaultCenter,
    defaultZoom,
    viewportPadding,
    showRoute = true,
    showRouteMarkers = true,
    hideOriginMarker = false,
    allowFitBounds = true,
    animateRoute = true,
    animateRouteDurationMs,
    showTrafficOverlay = false,
    showMapTypeControl = false,
    mapTypeControlPosition = 'top-right',
    trimRouteFrom,
    panTarget,
    panZoom,
    panAnimated = true,
    extraRoutes,
    htmlOverlays,
    fitToPoints,
    mapOptions,
    updateRouteInfo,
    onBoundsChange,
    flightArc,
    flightArcFlyoverKey,
    prefetchFlyoverTiles = false,
  } = props

  const apiKey = process.env.REACT_APP_HERE_MAPS_API_KEY
  const theme = useTheme()
  const themeMap = (theme as any).map ?? {}
  const [activeBaseLayer, setActiveBaseLayer] = React.useState<
    'map' | 'satellite'
  >(() => {
    try {
      const stored = window.sessionStorage.getItem('hereMap:baseLayer')
      return stored === 'satellite' ? 'satellite' : 'map'
    } catch {
      return 'map'
    }
  })
  const routeColor: string =
    activeBaseLayer === 'satellite'
      ? themeMap.routeColorSatellite || ROUTE_COLOR_DEFAULT
      : themeMap.routeColor || ROUTE_COLOR_DEFAULT
  const arcColorTheme: string =
    activeBaseLayer === 'satellite'
      ? themeMap.flightArcColorSatellite || '#ffd400'
      : themeMap.flightArcColor || ARC_COLOR_DEFAULT

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const mapRef = React.useRef<H.Map | null>(null)
  const platformRef = React.useRef<H.service.Platform | null>(null)
  const behaviorRef = React.useRef<H.mapevents.Behavior | null>(null)
  const omvProviderRef = React.useRef<any>(null)
  const trafficLayerRef = React.useRef<H.map.layer.Layer | null>(null)

  const routeGroupRef = React.useRef<H.map.Group | null>(null)
  const markerGroupRef = React.useRef<H.map.Group | null>(null)
  const circleGroupRef = React.useRef<H.map.Group | null>(null)
  const arcGroupRef = React.useRef<H.map.Group | null>(null)
  const extraRoutesGroupRef = React.useRef<H.map.Group | null>(null)

  const planeGroupRef = React.useRef<H.map.Group | null>(null)
  const arcPolylineRef = React.useRef<H.map.Polyline | null>(null)

  const onBoundsChangeRef = React.useRef(onBoundsChange)
  onBoundsChangeRef.current = onBoundsChange
  const updateRouteInfoRef = React.useRef(updateRouteInfo)
  updateRouteInfoRef.current = updateRouteInfo

  const [unavailable, setUnavailable] = React.useState(false)
  const [mapReady, setMapReady] = React.useState(false)
  const [isPreparingFlyover, setIsPreparingFlyover] = React.useState(false)
  const httpPrefetchReadyRef = React.useRef<Promise<void> | null>(null)
  const providerPrefetchReadyRef = React.useRef<Promise<void> | null>(null)
  const [routeSummary, setRouteSummary] =
    React.useState<HereRouteSummary | null>(null)
  const animatedSummaryRef = React.useRef<HereRouteSummary | null>(null)
  const routePolylineRef = React.useRef<H.map.Polyline | null>(null)
  const animatingRouteRef = React.useRef(false)
  const resolvedTrimFromRef = React.useRef<LatLngLiteral | null>(null)

  const resolvedOrigin = React.useMemo(
    () => (origin ? normaliseCoordinate(origin) : null),
    [origin],
  )
  const resolvedDestination = React.useMemo(
    () => (destination ? normaliseCoordinate(destination) : null),
    [destination],
  )
  const resolvedStops = React.useMemo(
    () =>
      additionalStops
        .map((s) => normaliseCoordinate(s))
        .filter((s): s is LatLngLiteral => !!s),
    [additionalStops],
  )
  const resolvedTrimFrom = React.useMemo(
    () => (trimRouteFrom ? normaliseCoordinate(trimRouteFrom) : null),
    [trimRouteFrom],
  )
  const resolvedPanTarget = React.useMemo(
    () => (panTarget ? normaliseCoordinate(panTarget) : null),
    [panTarget],
  )
  const resolvedArcFrom = React.useMemo(
    () => (flightArc?.from ? normaliseCoordinate(flightArc.from) : null),
    [flightArc?.from],
  )
  const resolvedArcTo = React.useMemo(
    () => (flightArc?.to ? normaliseCoordinate(flightArc.to) : null),
    [flightArc?.to],
  )

  const fallbackCenter = React.useMemo<LatLngLiteral>(() => {
    if (resolvedOrigin) return resolvedOrigin
    if (resolvedDestination) return resolvedDestination
    if (defaultCenter) return defaultCenter
    return getDefaultCenter()
  }, [resolvedOrigin, resolvedDestination, defaultCenter])

  const fallbackZoom = defaultZoom ?? mapOptions?.zoomLevel ?? FALLBACK_ZOOM

  React.useEffect(() => {
    if (!apiKey) {
      setUnavailable(true)
      return
    }
    const container = containerRef.current
    if (!container || mapRef.current) return

    let cancelled = false
    let teardown: (() => void) | undefined

    const init = () => {
      if (cancelled) return
      const platform = new H.service.Platform({ apikey: apiKey })
      platformRef.current = platform

      const defaultLayers = platform.createDefaultLayers({
        engineType: H.Map.EngineType.HARP,
      }) as unknown as {
        vector: { normal: { map: H.map.layer.Layer } }
        raster?: { satellite?: { map?: H.map.layer.Layer } }
      }
      const baseLayer = defaultLayers.vector.normal.map
      let satelliteLayer: H.map.layer.Layer | null =
        defaultLayers.raster?.satellite?.map ?? null
      if (!satelliteLayer) {
        try {
          const rasterLayers = platform.createDefaultLayers() as unknown as {
            raster?: { satellite?: { map?: H.map.layer.Layer } }
          }
          satelliteLayer = rasterLayers.raster?.satellite?.map ?? null
        } catch {
          /* raster layers unavailable */
        }
      }
      try {
        const provider = (baseLayer as any).getProvider?.()
        if (provider) omvProviderRef.current = provider
      } catch {
        /* provider unreachable; we'll fall back to HTTP cache only */
      }

      let persistedLayer: 'map' | 'satellite' = 'map'
      try {
        persistedLayer =
          window.sessionStorage.getItem('hereMap:baseLayer') === 'satellite'
            ? 'satellite'
            : 'map'
      } catch {
        /* swallow */
      }
      const initialBaseLayer =
        persistedLayer === 'satellite' && satelliteLayer
          ? satelliteLayer
          : baseLayer

      const map = new H.Map(container, initialBaseLayer, {
        engineType: H.Map.EngineType.HARP,
        pixelRatio: window.devicePixelRatio || 1,
        center: fallbackCenter,
        zoom: fallbackZoom,
      })
      mapRef.current = map

      const routeGroup = new H.map.Group()
      const markerGroup = new H.map.Group()
      const circleGroup = new H.map.Group()
      const arcGroup = new H.map.Group()
      const planeGroup = new H.map.Group()
      const extraRoutesGroup = new H.map.Group()
      map.addObject(routeGroup)
      map.addObject(extraRoutesGroup)
      map.addObject(arcGroup)
      map.addObject(circleGroup)
      map.addObject(markerGroup)
      map.addObject(planeGroup)
      routeGroupRef.current = routeGroup
      markerGroupRef.current = markerGroup
      circleGroupRef.current = circleGroup
      arcGroupRef.current = arcGroup
      planeGroupRef.current = planeGroup
      extraRoutesGroupRef.current = extraRoutesGroup

      let lastW = container.offsetWidth
      let lastH = container.offsetHeight
      const resize = () => {
        const w = container.offsetWidth
        const h = container.offsetHeight
        if (w === lastW && h === lastH) return
        lastW = w
        lastH = h
        try {
          map.getViewPort().resize()
        } catch {
          /* viewport mid-teardown */
        }
      }
      const ro =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(resize)
          : null
      ro?.observe(container)
      window.addEventListener('resize', resize)

      const events = new H.mapevents.MapEvents(map)
      const behavior = new H.mapevents.Behavior(events)
      behaviorRef.current = behavior
      if (mapOptions?.dragging === false) {
        behavior.disable(H.mapevents.Behavior.Feature.PANNING)
      }
      if (mapOptions?.scrollWheelZoom === false) {
        behavior.disable(H.mapevents.Behavior.Feature.WHEEL_ZOOM)
      }
      if (mapOptions?.doubleClickZoom === false) {
        behavior.disable(H.mapevents.Behavior.Feature.DBL_TAP_ZOOM)
      }
      if (mapOptions?.touchZoom === false) {
        behavior.disable(H.mapevents.Behavior.Feature.PINCH_ZOOM)
      }

      const reportBounds = () => {
        const cb = onBoundsChangeRef.current
        if (!cb) return
        try {
          const vm = map.getViewModel()
          const lookAt = vm.getLookAtData()
          const bounds = lookAt.bounds?.getBoundingBox?.()
          if (!bounds) return
          cb({
            north: bounds.getTop(),
            south: bounds.getBottom(),
            east: bounds.getRight(),
            west: bounds.getLeft(),
          })
        } catch {
          /* swallow */
        }
      }
      map.addEventListener('mapviewchangeend', reportBounds)

      let mapTypeControl: HTMLDivElement | null = null
      if (showMapTypeControl && satelliteLayer) {
        mapTypeControl = document.createElement('div')
        mapTypeControl.style.position = 'absolute'
        if (mapTypeControlPosition.startsWith('top')) {
          mapTypeControl.style.top = '12px'
        } else {
          mapTypeControl.style.bottom = '12px'
        }
        if (mapTypeControlPosition.endsWith('right')) {
          mapTypeControl.style.right = '12px'
        } else {
          mapTypeControl.style.left = '12px'
        }
        mapTypeControl.style.display = 'flex'
        mapTypeControl.style.background = '#ffffff'
        mapTypeControl.style.borderRadius = '6px'
        mapTypeControl.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)'
        mapTypeControl.style.fontFamily = 'system-ui, sans-serif'
        mapTypeControl.style.fontSize = '12px'
        mapTypeControl.style.fontWeight = '600'
        mapTypeControl.style.overflow = 'hidden'
        mapTypeControl.style.zIndex = '600'

        const makeBtn = (label: string, active: boolean) => {
          const btn = document.createElement('button')
          btn.type = 'button'
          btn.textContent = label
          btn.style.border = 'none'
          btn.style.padding = '6px 12px'
          btn.style.cursor = 'pointer'
          btn.style.background = active ? '#0b1c33' : '#ffffff'
          btn.style.color = active ? '#ffffff' : '#0b1c33'
          btn.style.fontFamily = 'inherit'
          btn.style.fontSize = 'inherit'
          btn.style.fontWeight = 'inherit'
          return btn
        }
        const mapBtn = makeBtn('Map', persistedLayer === 'map')
        const satBtn = makeBtn('Satellite', persistedLayer === 'satellite')

        const setActive = (which: 'map' | 'satellite') => {
          const isMap = which === 'map'
          mapBtn.style.background = isMap ? '#0b1c33' : '#ffffff'
          mapBtn.style.color = isMap ? '#ffffff' : '#0b1c33'
          satBtn.style.background = isMap ? '#ffffff' : '#0b1c33'
          satBtn.style.color = isMap ? '#0b1c33' : '#ffffff'
          try {
            map.setBaseLayer(
              isMap ? baseLayer : (satelliteLayer as H.map.layer.Layer),
            )
          } catch {
            /* swallow */
          }
          try {
            window.sessionStorage.setItem('hereMap:baseLayer', which)
          } catch {
            /* swallow */
          }
          setActiveBaseLayer(which)
        }
        mapBtn.addEventListener('click', () => setActive('map'))
        satBtn.addEventListener('click', () => setActive('satellite'))
        mapTypeControl.appendChild(mapBtn)
        mapTypeControl.appendChild(satBtn)
        container.appendChild(mapTypeControl)
      }

      teardown = () => {
        map.removeEventListener('mapviewchangeend', reportBounds)
        try {
          mapTypeControl?.remove()
        } catch {
          /* swallow */
        }
        window.removeEventListener('resize', resize)
        ro?.disconnect()
        behavior.dispose()
        events.dispose()
        try {
          map.dispose()
        } catch {
          /* swallow */
        }
        mapRef.current = null
        behaviorRef.current = null
        platformRef.current = null
        omvProviderRef.current = null
        trafficLayerRef.current = null
        routeGroupRef.current = null
        markerGroupRef.current = null
        circleGroupRef.current = null
        arcGroupRef.current = null
        planeGroupRef.current = null
        extraRoutesGroupRef.current = null
        arcPolylineRef.current = null
      }

      setMapReady(true)
    }

    const initWhenSized = () => {
      if (cancelled) return
      if (container.offsetWidth === 0 || container.offsetHeight === 0) {
        requestAnimationFrame(initWhenSized)
        return
      }
      init()
    }
    initWhenSized()

    return () => {
      cancelled = true
      setMapReady(false)
      teardown?.()
    }
  }, [apiKey])

  React.useEffect(() => {
    const map = mapRef.current
    if (!apiKey || !map) return

    if (showTrafficOverlay && !trafficLayerRef.current) {
      const template = buildHereTrafficTileUrl(apiKey)
      const provider = new H.map.provider.ImageTileProvider({
        getURL: (x: number, y: number, z: number) =>
          template
            .replace('{z}', String(z))
            .replace('{x}', String(x))
            .replace('{y}', String(y)),
        tileSize: 512,
      })
      const layer = new H.map.layer.TileLayer(provider)
      map.addLayer(layer)
      trafficLayerRef.current = layer
    } else if (!showTrafficOverlay && trafficLayerRef.current) {
      try {
        map.removeLayer(trafficLayerRef.current)
      } catch {
        /* layer already detached */
      }
      trafficLayerRef.current = null
    }
  }, [apiKey, showTrafficOverlay, mapReady])

  React.useEffect(() => {
    if (!apiKey) return
    if (!showRoute || !resolvedOrigin || !resolvedDestination) {
      setRouteSummary(null)
      return
    }
    let cancelled = false
    fetchHereRoute({
      apiKey,
      origin: resolvedOrigin,
      destination: resolvedDestination,
      via: resolvedStops,
    })
      .then((summary) => {
        if (cancelled) return
        setRouteSummary(summary)
        const cb = updateRouteInfoRef.current
        if (cb && summary) {
          cb({
            estimatedJourneyTime: summary.durationSeconds,
            estimatedJourneyDistance: metersToMiles(summary.lengthMeters),
            directionsResponse: summary.raw,
          })
        }
      })
      .catch(() => {
        if (cancelled) return
        setRouteSummary(null)
      })
    return () => {
      cancelled = true
    }
  }, [
    apiKey,
    showRoute,
    resolvedOrigin?.lat,
    resolvedOrigin?.lng,
    resolvedDestination?.lat,
    resolvedDestination?.lng,
    JSON.stringify(resolvedStops),
  ])

  React.useEffect(() => {
    resolvedTrimFromRef.current = resolvedTrimFrom
  }, [resolvedTrimFrom])

  const buildFullPath = React.useCallback(
    (summary: HereRouteSummary, trim: LatLngLiteral | null) => {
      const segs = trim
        ? trimPolylinesFromPoint(summary.polylines, trim)
        : summary.polylines
      const fullPath: LatLngLiteral[] = []
      for (const segment of segs) {
        for (const pt of segment) {
          const last = fullPath[fullPath.length - 1]
          if (!last || last.lat !== pt.lat || last.lng !== pt.lng) {
            fullPath.push(pt)
          }
        }
      }
      return fullPath
    },
    [],
  )

  React.useEffect(() => {
    if (!mapReady) return
    const group = routeGroupRef.current
    if (!group) return
    group.removeAll()
    routePolylineRef.current = null
    animatingRouteRef.current = false
    if (!routeSummary || !showRoute) return

    const fullPath = buildFullPath(routeSummary, resolvedTrimFromRef.current)
    if (fullPath.length < 2) return

    const isNewRoute = animatedSummaryRef.current !== routeSummary
    const shouldAnimate = animateRoute && isNewRoute
    animatedSummaryRef.current = routeSummary

    const style = {
      strokeColor: routeColor,
      lineWidth: ROUTE_WIDTH_DEFAULT,
      lineCap: 'round' as const,
    }

    if (!shouldAnimate) {
      const polyline = new H.map.Polyline(toLineString(fullPath), {
        data: null,
        style,
      })
      group.addObject(polyline)
      routePolylineRef.current = polyline
      return
    }

    const totalLength = getPolylineLength(fullPath)
    if (totalLength <= 0) return

    const duration =
      animateRouteDurationMs && animateRouteDurationMs > 0
        ? animateRouteDurationMs
        : Math.min(2400, Math.max(1400, totalLength * 30000))

    const startTs = performance.now()
    let rafId = 0
    let cancelled = false
    animatingRouteRef.current = true

    const polyline = new H.map.Polyline(toLineString(fullPath.slice(0, 2)), {
      data: null,
      style,
    })
    group.addObject(polyline)
    routePolylineRef.current = polyline

    const tick = (now: number) => {
      if (cancelled) return
      const progress = Math.min(1, (now - startTs) / duration)
      const pts =
        progress >= 1
          ? fullPath
          : clipPolylineToDistance(fullPath, totalLength * progress)
      if (pts.length >= 2) {
        try {
          polyline.setGeometry(toLineString(pts))
          ;(polyline as any).invalidate?.(2)
        } catch {
          /* swallow */
        }
      }
      if (progress < 1) {
        rafId = requestAnimationFrame(tick)
      } else {
        animatingRouteRef.current = false
      }
    }
    rafId = requestAnimationFrame(tick)

    return () => {
      cancelled = true
      animatingRouteRef.current = false
      cancelAnimationFrame(rafId)
    }
  }, [
    mapReady,
    routeSummary,
    showRoute,
    routeColor,
    animateRoute,
    animateRouteDurationMs,
    buildFullPath,
  ])

  React.useEffect(() => {
    if (!mapReady) return
    if (animatingRouteRef.current) return
    const polyline = routePolylineRef.current
    const group = routeGroupRef.current
    if (!polyline || !group || !routeSummary) return
    const fullPath = buildFullPath(routeSummary, resolvedTrimFrom)
    if (fullPath.length < 2) return
    try {
      const next = new H.map.Polyline(toLineString(fullPath), {
        data: null,
        style: {
          strokeColor: routeColor,
          lineWidth: ROUTE_WIDTH_DEFAULT,
          lineCap: 'round',
        },
      })
      group.addObject(next)
      group.removeObject(polyline)
      routePolylineRef.current = next
    } catch {
      /* swallow */
    }
  }, [
    mapReady,
    routeSummary,
    routeColor,
    resolvedTrimFrom?.lat,
    resolvedTrimFrom?.lng,
    buildFullPath,
  ])

  const extraRoutesKey = React.useMemo(() => {
    if (!extraRoutes?.length) return ''
    return extraRoutes
      .map((r) => {
        const o = normaliseCoordinate(r.origin)
        const d = normaliseCoordinate(r.destination)
        return `${r.key}|${o?.lat ?? ''},${o?.lng ?? ''}>${d?.lat ?? ''},${
          d?.lng ?? ''
        }`
      })
      .join(';')
  }, [extraRoutes])

  const [extraRouteSummaries, setExtraRouteSummaries] = React.useState<
    Record<string, HereRouteSummary>
  >({})

  type OverlayLayout = {
    markerX: number
    markerY: number
    anchorX: number
    anchorY: number
    tailSide: 'top' | 'right' | 'bottom' | 'left'
    tailAttach: number
  }
  const [overlayLayouts, setOverlayLayouts] = React.useState<
    Record<string, OverlayLayout>
  >({})

  const overlayPositionsKey = React.useMemo(() => {
    if (!htmlOverlays?.length) return ''
    return htmlOverlays
      .map((o) => {
        const p = normaliseCoordinate(o.position)
        const a = o.preferAwayFrom
          ? normaliseCoordinate(o.preferAwayFrom)
          : null
        return `${o.key}|${p?.lat ?? ''},${p?.lng ?? ''}|${a?.lat ?? ''},${
          a?.lng ?? ''
        }|${o.size.width}x${o.size.height}|${o.tailHeight ?? 12}`
      })
      .join(';')
  }, [htmlOverlays])

  React.useEffect(() => {
    if (!mapReady) {
      setOverlayLayouts({})
      return
    }
    const map = mapRef.current
    if (!map) return
    if (!htmlOverlays?.length) {
      setOverlayLayouts({})
      return
    }

    const recompute = () => {
      const next: Record<string, OverlayLayout> = {}
      const placed: Array<{
        left: number
        right: number
        top: number
        bottom: number
      }> = []

      // Each destination pin is a fixed obstacle: a card must never cover
      // another overlay's circle.
      const MARKER_R = 12
      const markerBoxes: Array<{
        key: string
        left: number
        right: number
        top: number
        bottom: number
      }> = []
      for (const o of htmlOverlays) {
        const pt = normaliseCoordinate(o.position)
        if (!pt) continue
        try {
          const s = map.geoToScreen({
            lat: pt.lat,
            lng: pt.lng,
          } as H.geo.IPoint)
          if (s)
            markerBoxes.push({
              key: o.key,
              left: s.x - MARKER_R,
              right: s.x + MARKER_R,
              top: s.y - MARKER_R,
              bottom: s.y + MARKER_R,
            })
        } catch {
          /* swallow */
        }
      }

      for (const o of htmlOverlays) {
        const pt = normaliseCoordinate(o.position)
        if (!pt) continue
        let markerScreen: { x: number; y: number } | null = null
        try {
          const s = map.geoToScreen({
            lat: pt.lat,
            lng: pt.lng,
          } as H.geo.IPoint)
          if (s) markerScreen = { x: s.x, y: s.y }
        } catch {
          /* swallow */
        }
        if (!markerScreen) continue

        let awayScreen: { x: number; y: number } | null = null
        if (o.preferAwayFrom) {
          const ap = normaliseCoordinate(o.preferAwayFrom)
          if (ap) {
            try {
              const s = map.geoToScreen({
                lat: ap.lat,
                lng: ap.lng,
              } as H.geo.IPoint)
              if (s) awayScreen = { x: s.x, y: s.y }
            } catch {
              /* swallow */
            }
          }
        }

        const w = o.size.width
        const h = o.size.height
        const halfW = w / 2
        const halfH = h / 2
        const tail = o.tailHeight ?? 12

        let dir: { x: number; y: number }
        if (awayScreen) {
          const dx = markerScreen.x - awayScreen.x
          const dy = markerScreen.y - awayScreen.y
          const len = Math.hypot(dx, dy) || 1
          dir = { x: dx / len, y: dy / len }
        } else {
          dir = { x: 0, y: -1 }
        }

        const layoutForDir = (d: { x: number; y: number }) => {
          const useVertical =
            Math.abs(d.y) * (halfW + tail) >= Math.abs(d.x) * (halfH + tail)
          let side: 'top' | 'right' | 'bottom' | 'left'
          let attach: number
          let cx: number
          let cy: number
          if (useVertical) {
            side = d.y < 0 ? 'bottom' : 'top'
            const offsetY = (d.y < 0 ? -1 : 1) * (halfH + tail)
            const offsetX = Math.abs(d.y) > 1e-6 ? (offsetY * d.x) / d.y : 0
            const clampedX = Math.max(
              -halfW * 0.85,
              Math.min(halfW * 0.85, offsetX),
            )
            attach = 0.5 - clampedX / w
            cx = markerScreen!.x + clampedX
            cy = markerScreen!.y + offsetY
          } else {
            side = d.x < 0 ? 'right' : 'left'
            const offsetX = (d.x < 0 ? -1 : 1) * (halfW + tail)
            const offsetY = Math.abs(d.x) > 1e-6 ? (offsetX * d.y) / d.x : 0
            const clampedY = Math.max(
              -halfH * 0.85,
              Math.min(halfH * 0.85, offsetY),
            )
            attach = 0.5 - clampedY / h
            cx = markerScreen!.x + offsetX
            cy = markerScreen!.y + clampedY
          }
          attach = Math.max(0.08, Math.min(0.92, attach))
          const bbox = {
            left: cx - halfW,
            right: cx + halfW,
            top: cy - halfH,
            bottom: cy + halfH,
          }
          return { side, attach, bbox }
        }

        const overlaps = (
          bb: { left: number; right: number; top: number; bottom: number },
          p: { left: number; right: number; top: number; bottom: number },
          gap: number,
        ) =>
          bb.right + gap > p.left &&
          bb.left < p.right + gap &&
          bb.bottom + gap > p.top &&
          bb.top < p.bottom + gap

        const collides = (
          bb: { left: number; right: number; top: number; bottom: number },
          gap = 8,
        ) =>
          placed.some((p) => overlaps(bb, p, gap)) ||
          markerBoxes.some((m) => m.key !== o.key && overlaps(bb, m, 0))

        let chosen = layoutForDir(dir)
        let anchorX = markerScreen.x
        let anchorY = markerScreen.y
        if (collides(chosen.bbox)) {
          const STEP = (10 * Math.PI) / 180
          let placedThisRound = false
          for (let step = 1; step <= 18 && !placedThisRound; step++) {
            for (const sign of [1, -1]) {
              const a = step * STEP * sign
              const cosA = Math.cos(a)
              const sinA = Math.sin(a)
              const nd = {
                x: dir.x * cosA - dir.y * sinA,
                y: dir.x * sinA + dir.y * cosA,
              }
              const cand = layoutForDir(nd)
              if (!collides(cand.bbox)) {
                chosen = cand
                placedThisRound = true
                break
              }
            }
          }

          if (!placedThisRound) {
            const centre = (b: {
              left: number
              right: number
              top: number
              bottom: number
            }) => ({ x: (b.left + b.right) / 2, y: (b.top + b.bottom) / 2 })
            const c0 = centre(chosen.bbox)
            const hits = [
              ...placed,
              ...markerBoxes.filter((m) => m.key !== o.key),
            ].filter(
              (p) =>
                chosen.bbox.right + 8 > p.left &&
                chosen.bbox.left < p.right + 8 &&
                chosen.bbox.bottom + 8 > p.top &&
                chosen.bbox.top < p.bottom + 8,
            )
            let ux = 0
            let uy = 0
            hits.forEach((p) => {
              const pc = centre(p)
              ux += c0.x - pc.x
              uy += c0.y - pc.y
            })
            let ul = Math.hypot(ux, uy)
            if (ul < 1e-3) {
              ux = c0.x - markerScreen.x
              uy = c0.y - markerScreen.y
              ul = Math.hypot(ux, uy) || 1
            }
            ux /= ul
            uy /= ul
            for (let extra = 24; extra <= 480; extra += 24) {
              const shifted = {
                left: chosen.bbox.left + ux * extra,
                right: chosen.bbox.right + ux * extra,
                top: chosen.bbox.top + uy * extra,
                bottom: chosen.bbox.bottom + uy * extra,
              }
              if (!collides(shifted)) {
                chosen = {
                  side: chosen.side,
                  attach: chosen.attach,
                  bbox: shifted,
                }
                anchorX = markerScreen.x + ux * extra
                anchorY = markerScreen.y + uy * extra
                break
              }
            }
          }
        }

        next[o.key] = {
          markerX: markerScreen.x,
          markerY: markerScreen.y,
          anchorX,
          anchorY,
          tailSide: chosen.side,
          tailAttach: chosen.attach,
        }
        placed.push(chosen.bbox)
      }
      setOverlayLayouts(next)
    }

    recompute()
    let rafId = 0
    const onViewChange = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        recompute()
      })
    }
    map.addEventListener('mapviewchange', onViewChange)
    map.addEventListener('mapviewchangeend', recompute)
    return () => {
      map.removeEventListener('mapviewchange', onViewChange)
      map.removeEventListener('mapviewchangeend', recompute)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [mapReady, overlayPositionsKey])

  React.useEffect(() => {
    if (!apiKey) return
    if (!extraRoutes?.length) {
      setExtraRouteSummaries({})
      return
    }
    let cancelled = false
    const ac = new AbortController()
    Promise.all(
      extraRoutes.map(async (r) => {
        const o = normaliseCoordinate(r.origin)
        const d = normaliseCoordinate(r.destination)
        if (!o || !d) return null
        try {
          const summary = await fetchHereRoute({
            apiKey,
            origin: o,
            destination: d,
            signal: ac.signal,
          })
          return [r.key, summary] as const
        } catch {
          return null
        }
      }),
    ).then((results) => {
      if (cancelled) return
      const next: Record<string, HereRouteSummary> = {}
      for (const entry of results) if (entry) next[entry[0]] = entry[1]
      setExtraRouteSummaries(next)
    })
    return () => {
      cancelled = true
      ac.abort()
    }
  }, [apiKey, extraRoutesKey])

  React.useEffect(() => {
    if (!mapReady) return
    const group = extraRoutesGroupRef.current
    if (!group) return
    group.removeAll()
    if (!extraRoutes?.length) return
    for (const r of extraRoutes) {
      const summary = extraRouteSummaries[r.key]
      if (!summary) continue
      const fullPath: LatLngLiteral[] = []
      for (const seg of summary.polylines) {
        for (const pt of seg) {
          const last = fullPath[fullPath.length - 1]
          if (!last || last.lat !== pt.lat || last.lng !== pt.lng) {
            fullPath.push(pt)
          }
        }
      }
      if (fullPath.length < 2) continue
      const polyline = new H.map.Polyline(toLineString(fullPath), {
        data: null,
        style: {
          strokeColor: r.color || routeColor,
          lineWidth: ROUTE_WIDTH_DEFAULT,
          lineCap: 'round',
        },
      })
      group.addObject(polyline)
    }
  }, [mapReady, extraRoutes, extraRouteSummaries, routeColor])

  React.useEffect(() => {
    if (!mapReady) return
    const group = markerGroupRef.current
    if (!group) return
    group.removeAll()

    const addKindMarker = (
      pt: LatLngLiteral,
      kind: MarkerKind,
      label?: string | number,
      onClick?: () => void,
    ) => {
      const marker = new H.map.DomMarker(
        { lat: pt.lat, lng: pt.lng } as H.geo.IPoint,
        {
          data: null,
          icon: getKindIcon(kind, label),
        },
      )
      if (onClick) {
        marker.addEventListener('tap', () => onClick())
      }
      group.addObject(marker)
    }

    if (showRouteMarkers) {
      if (resolvedOrigin && !hideOriginMarker) {
        addKindMarker(resolvedOrigin, 'origin')
      }
      if (resolvedDestination) {
        addKindMarker(resolvedDestination, 'destination')
      }
      resolvedStops.forEach((s, i) => {
        addKindMarker(s, 'stop', i + 1)
      })
    }

    for (const m of markers) {
      const pt = normaliseCoordinate(m.position)
      if (!pt) continue
      addKindMarker(pt, m.kind ?? 'dot', m.label, m.onClick)
    }
  }, [
    mapReady,
    resolvedOrigin?.lat,
    resolvedOrigin?.lng,
    resolvedDestination?.lat,
    resolvedDestination?.lng,
    JSON.stringify(resolvedStops),
    JSON.stringify(
      markers.map((m) => ({
        lat: normaliseCoordinate(m.position)?.lat,
        lng: normaliseCoordinate(m.position)?.lng,
        kind: m.kind,
        label: m.label,
      })),
    ),
    showRouteMarkers,
    hideOriginMarker,
  ])

  React.useEffect(() => {
    if (!mapReady) return
    const group = circleGroupRef.current
    if (!group) return
    group.removeAll()
    for (const c of circles) {
      const pt = normaliseCoordinate(c.position)
      if (!pt) continue
      const circle = new H.map.Circle(
        { lat: pt.lat, lng: pt.lng } as H.geo.IPoint,
        c.radius ?? 100,
        {
          data: null,
          style: new H.map.SpatialStyle({
            strokeColor: c.strokeColor ?? '#0b1c33',
            lineWidth: c.strokeWeight ?? 2,
            fillColor: c.fillColor ?? 'rgba(11,28,51,0.15)',
          }),
        },
      )
      if (c.onClick) {
        circle.addEventListener('tap', () => c.onClick?.())
      }
      group.addObject(circle)
    }
  }, [mapReady, JSON.stringify(circles)])

  React.useEffect(() => {
    if (!mapReady) return
    const group = arcGroupRef.current
    if (!group) return
    group.removeAll()
    arcPolylineRef.current = null
    if (!resolvedArcFrom || !resolvedArcTo) return

    const arcColor = flightArc?.color ?? arcColorTheme
    const points = sampleBezier(resolvedArcFrom, resolvedArcTo, 64)
    const polyline = new H.map.Polyline(toLineString(points), {
      data: null,
      style: {
        strokeColor: arcColor,
        lineWidth: 4,
        lineDash: [1, 8],
        lineCap: 'round',
      },
    })
    group.addObject(polyline)
    arcPolylineRef.current = polyline
  }, [
    mapReady,
    resolvedArcFrom?.lat,
    resolvedArcFrom?.lng,
    resolvedArcTo?.lat,
    resolvedArcTo?.lng,
    flightArc?.color,
    arcColorTheme,
  ])

  const flyoverPathPlan = React.useMemo(() => {
    if (!resolvedArcFrom || !resolvedArcTo) return null
    const from = resolvedArcFrom
    const to = resolvedArcTo

    const distanceKm = haversineKm(from, to)
    const closeZoom = 11
    const estimatedMidZoom = computeCruiseZoom(distanceKm, closeZoom)
    const startZoom = Math.min(closeZoom, estimatedMidZoom + 3)

    const dx = to.lng - from.lng
    const dy = to.lat - from.lat
    const midLat = (from.lat + to.lat) / 2
    const midLng = (from.lng + to.lng) / 2
    const ctrlLat = midLat + -dx * 0.22
    const ctrlLng = midLng + dy * 0.22
    const bezierAtLocal = (s: number): LatLngLiteral => {
      const om = 1 - s
      return {
        lat: om * om * from.lat + 2 * om * s * ctrlLat + s * s * to.lat,
        lng: om * om * from.lng + 2 * om * s * ctrlLng + s * s * to.lng,
      }
    }

    const densePath: LatLngLiteral[] = []
    for (let i = 0; i <= 64; i++) densePath.push(bezierAtLocal(i / 64))

    const minZ = Math.max(2, Math.floor(estimatedMidZoom))
    const maxZ = Math.max(
      Math.ceil(startZoom),
      Math.ceil(estimatedMidZoom),
      closeZoom + 1,
    )
    const allZooms: number[] = []
    for (let z = minZ; z <= maxZ; z++) allZooms.push(z)

    return { from, to, densePath, allZooms, closeZoom }
  }, [
    resolvedArcFrom?.lat,
    resolvedArcFrom?.lng,
    resolvedArcTo?.lat,
    resolvedArcTo?.lng,
  ])

  React.useEffect(() => {
    if (!prefetchFlyoverTiles) return
    if (!apiKey) return
    if (!flyoverPathPlan) return
    const { from, to, densePath, allZooms } = flyoverPathPlan
    const handles = [
      prefetchHereVectorTiles(densePath, allZooms, apiKey, 1, 30000),
      prefetchHereVectorTiles([from, to], allZooms, apiKey, 2, 30000),
    ]
    httpPrefetchReadyRef.current = Promise.all(
      handles.map((h) => h.promise),
    ).then(() => undefined)
    return () => {
      handles.forEach((h) => h.abort())
      httpPrefetchReadyRef.current = null
    }
  }, [apiKey, flyoverPathPlan, prefetchFlyoverTiles])

  React.useEffect(() => {
    if (!prefetchFlyoverTiles) return
    if (!mapReady) return
    if (!flyoverPathPlan) return
    const provider = omvProviderRef.current
    if (!provider || typeof provider.requestInternal !== 'function') return
    const { from, to, densePath, allZooms, closeZoom } = flyoverPathPlan
    const PER_TILE_TIMEOUT_MS = 30000
    const providerPromises: Promise<void>[] = []
    const seen = new Set<string>()
    const warm = (
      coords: LatLngLiteral[],
      zooms: number[],
      padding: number,
    ) => {
      for (const z of zooms) {
        const n = 2 ** z
        for (const { lat, lng } of coords) {
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
          const latRad = (lat * Math.PI) / 180
          const tx = Math.floor(((lng + 180) / 360) * n)
          const ty = Math.floor(
            ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n,
          )
          for (let dx2 = -padding; dx2 <= padding; dx2++) {
            for (let dy2 = -padding; dy2 <= padding; dy2++) {
              const x = (((tx + dx2) % n) + n) % n
              const y = ty + dy2
              if (y < 0 || y >= n) continue
              const key = `${z}/${x}/${y}`
              if (seen.has(key)) continue
              seen.add(key)
              providerPromises.push(
                new Promise<void>((resolve) => {
                  let settled = false
                  const finish = () => {
                    if (settled) return
                    settled = true
                    resolve()
                  }
                  const safety = window.setTimeout(finish, PER_TILE_TIMEOUT_MS)
                  const done = () => {
                    window.clearTimeout(safety)
                    finish()
                  }
                  try {
                    provider.requestInternal(x, y, z, done, done)
                  } catch {
                    done()
                  }
                }),
              )
            }
          }
        }
      }
    }
    warm(densePath, allZooms, 1)
    warm([from, to], [closeZoom - 1, closeZoom], 2)
    providerPrefetchReadyRef.current = Promise.all(providerPromises).then(
      () => undefined,
    )
    return () => {
      providerPrefetchReadyRef.current = null
    }
  }, [mapReady, flyoverPathPlan, prefetchFlyoverTiles])

  React.useEffect(() => {
    if (!mapReady || !allowFitBounds) return
    const map = mapRef.current
    if (!map) return

    const points: LatLngLiteral[] = []
    if (fitToPoints?.length) {
      for (const p of fitToPoints) {
        const np = normaliseCoordinate(p)
        if (np) points.push(np)
      }
    } else {
      if (resolvedOrigin) points.push(resolvedOrigin)
      if (resolvedDestination) points.push(resolvedDestination)
      points.push(...resolvedStops)
      if (routeSummary) {
        for (const seg of routeSummary.polylines) points.push(...seg)
      }
    }
    if (points.length === 0) return

    if (points.length === 1) {
      map.setCenter(points[0])
      map.setZoom(fallbackZoom)
      return
    }

    try {
      const rect = H.geo.Rect.coverPoints(
        points.map((p) => ({ lat: p.lat, lng: p.lng } as H.geo.IPoint)),
      )
      if (!rect) return
      map
        .getViewPort()
        .setPadding(
          viewportPadding?.top ?? 32,
          viewportPadding?.right ?? 32,
          viewportPadding?.bottom ?? 32,
          viewportPadding?.left ?? 32,
        )
      map.getViewModel().setLookAtData({ bounds: rect }, false)
    } catch {
      /* swallow if bounds invalid */
    }
  }, [
    mapReady,
    allowFitBounds,
    resolvedOrigin?.lat,
    resolvedOrigin?.lng,
    resolvedDestination?.lat,
    resolvedDestination?.lng,
    JSON.stringify(resolvedStops),
    routeSummary,
    fallbackZoom,
    JSON.stringify(
      fitToPoints?.map((p) => {
        const np = normaliseCoordinate(p)
        return np ? [np.lat, np.lng] : null
      }),
    ),
    viewportPadding?.top,
    viewportPadding?.right,
    viewportPadding?.bottom,
    viewportPadding?.left,
  ])

  React.useEffect(() => {
    if (!mapReady) return
    if (!resolvedPanTarget) return
    const map = mapRef.current
    if (!map) return
    try {
      map
        .getViewPort()
        .setPadding(
          viewportPadding?.top ?? 32,
          viewportPadding?.right ?? 32,
          viewportPadding?.bottom ?? 32,
          viewportPadding?.left ?? 32,
        )
      map.getViewModel().setLookAtData(
        {
          position: {
            lat: resolvedPanTarget.lat,
            lng: resolvedPanTarget.lng,
          } as H.geo.IPoint,
          zoom: panZoom ?? map.getZoom(),
        },
        panAnimated,
      )
    } catch {
      /* swallow */
    }
  }, [
    mapReady,
    resolvedPanTarget?.lat,
    resolvedPanTarget?.lng,
    panZoom,
    panAnimated,
    viewportPadding?.top,
    viewportPadding?.right,
    viewportPadding?.bottom,
    viewportPadding?.left,
  ])

  const flyoverActiveRef = React.useRef(false)
  const lastFlyoverKeyRef = React.useRef<number | undefined>(undefined)
  React.useEffect(() => {
    if (!mapReady) return
    if (flightArcFlyoverKey == null) return
    if (lastFlyoverKeyRef.current === flightArcFlyoverKey) return
    lastFlyoverKeyRef.current = flightArcFlyoverKey
    perfMark('flyover:start')
    const map = mapRef.current
    const arcGroup = arcGroupRef.current
    const planeGroup = planeGroupRef.current
    if (!map || !arcGroup || !planeGroup) return

    planeGroup.removeAll()
    if (!resolvedArcFrom || !resolvedArcTo) return
    if (flyoverActiveRef.current) return
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }

    const from = resolvedArcFrom
    const to = resolvedArcTo
    const arcColor = flightArc?.color ?? arcColorTheme

    const SAMPLES = 200
    const path = sampleBezier(from, to, SAMPLES)

    const closeZoom = 11
    const midZoom = computeCruiseZoom(haversineKm(from, to), closeZoom)

    const staticArc = arcPolylineRef.current
    if (staticArc) {
      arcGroup.removeObject(staticArc)
      arcPolylineRef.current = null
    }

    const progressivePolyline = new H.map.Polyline(
      toLineString(path.slice(0, 2)),
      {
        data: null,
        style: {
          strokeColor: arcColor,
          lineWidth: 4,
          lineDash: [1, 8],
          lineCap: 'round',
        },
      },
    )
    arcGroup.addObject(progressivePolyline)
    arcPolylineRef.current = progressivePolyline

    const behavior = behaviorRef.current
    const PAN_BIT = H.mapevents.Behavior.Feature.PANNING
    const WHEEL_BIT = H.mapevents.Behavior.Feature.WHEEL_ZOOM
    const PINCH_BIT = H.mapevents.Behavior.Feature.PINCH_ZOOM
    const DBL_BIT = H.mapevents.Behavior.Feature.DBL_TAP_ZOOM
    behavior?.disable(PAN_BIT | WHEEL_BIT | PINCH_BIT | DBL_BIT)

    const dx = to.lng - from.lng
    const dy = to.lat - from.lat
    const midLat = (from.lat + to.lat) / 2
    const midLng = (from.lng + to.lng) / 2
    const ctrlLat = midLat + -dx * 0.22
    const ctrlLng = midLng + dy * 0.22
    const bezierAt = (s: number): LatLngLiteral => {
      const om = 1 - s
      return {
        lat: om * om * from.lat + 2 * om * s * ctrlLat + s * s * to.lat,
        lng: om * om * from.lng + 2 * om * s * ctrlLng + s * s * to.lng,
      }
    }

    const container = containerRef.current
    if (!container) return
    const planeColor = arcColor
    const shadowEl = buildShadowOverlay()
    const planeEl = buildPlaneOverlay(planeColor)
    container.appendChild(shadowEl)
    container.appendChild(planeEl)

    const planeState = { geo: from, bearing: 0 }
    const shadowState = { geo: from, bearing: 0 }

    const positionOverlay = (
      el: HTMLDivElement,
      pt: LatLngLiteral,
      bearing: number,
    ) => {
      const screen = map.geoToScreen({ lat: pt.lat, lng: pt.lng })
      if (!screen) {
        el.style.display = 'none'
        return
      }
      el.style.display = 'block'
      el.style.transform = `translate(${screen.x}px, ${screen.y}px) rotate(${bearing}deg)`
    }

    const repositionFromState = () => {
      positionOverlay(planeEl, planeState.geo, planeState.bearing)
      positionOverlay(shadowEl, shadowState.geo, shadowState.bearing)
    }

    const initialBearing = computeBearing(from, bezierAt(0.01))
    planeState.geo = from
    planeState.bearing = initialBearing
    shadowState.geo = from
    shadowState.bearing = initialBearing
    repositionFromState()

    flyoverActiveRef.current = true
    const distanceKm = haversineKm(from, to)
    const REF_KM = 1000
    const BASE_MS = 6000
    const MIN_MS = 5000
    const MAX_MS = 6000
    const K = (BASE_MS - MIN_MS) / Math.log10(8) // ≈ 3322
    const raw = BASE_MS - K * Math.log10(Math.max(distanceKm, 1) / REF_KM)
    const totalMs = Math.max(MIN_MS, Math.min(MAX_MS, raw))
    let startTs = 0
    let rafId = 0
    let lastIdx = 1
    let lastPhase: 'descent' | 'cruise' | 'ascent' | 'end' = 'descent'
    let landedMarker: H.map.DomMarker | null = null

    const vm = map.getViewModel()

    const tick = (now: number) => {
      if (startTs === 0) {
        startTs = now
        perfMark('flyover:first-frame')
        perfMeasure(
          'flyover:rAF-to-first-frame',
          'flyover:rAF-start',
          'flyover:first-frame',
        )
      }
      perfMark('flyover:frame:start')
      const t = Math.min(1, (now - startTs) / totalMs)
      const eased = easeInOutCubic(t)

      const cam = bezierAt(eased)

      const POLYLINE_STRIDE = 3
      const idx = Math.max(1, Math.floor(eased * SAMPLES))
      const sinceLast = idx - lastIdx
      const isLastFrame = t >= 1
      if ((sinceLast >= POLYLINE_STRIDE || isLastFrame) && sinceLast > 0) {
        lastIdx = idx
        try {
          progressivePolyline.setGeometry(
            toLineString([...path.slice(0, Math.max(2, idx + 1)), cam]),
          )
        } catch {
          /* mid-teardown */
        }
      }

      const startZoom = Math.min(closeZoom, midZoom + 3)
      let zoom: number
      let phase: 'descent' | 'cruise' | 'ascent'
      if (eased < 0.18) {
        const k = eased / 0.18
        zoom = startZoom - (startZoom - midZoom) * k
        phase = 'descent'
      } else if (eased < 0.78) {
        zoom = midZoom
        phase = 'cruise'
      } else {
        const k = (eased - 0.78) / 0.22
        zoom = midZoom + (closeZoom - midZoom) * k
        phase = 'ascent'
      }
      if (phase !== lastPhase) {
        perfMark(`flyover:phase:${phase}`)
        if (lastPhase) {
          perfMeasure(
            `flyover:phase:${lastPhase}`,
            `flyover:phase:${lastPhase}`,
            `flyover:phase:${phase}`,
          )
        }
        lastPhase = phase
      }

      let bearing: number
      if (eased >= 0.999) {
        const behind = bezierAt(0.99)
        bearing = computeBearing(behind, cam)
      } else {
        const ahead = bezierAt(Math.min(1, eased + 0.01))
        bearing = computeBearing(cam, ahead)
      }

      try {
        vm.setLookAtData(
          {
            position: { lat: cam.lat, lng: cam.lng } as H.geo.IPoint,
            zoom,
          },
          false,
        )
      } catch {
        /* swallow */
      }

      const shadowLat = from.lat + (to.lat - from.lat) * eased
      const shadowLng = from.lng + (to.lng - from.lng) * eased

      planeState.geo = cam
      planeState.bearing = bearing
      shadowState.geo = { lat: shadowLat, lng: shadowLng }
      shadowState.bearing = bearing
      positionOverlay(planeEl, cam, bearing)
      positionOverlay(shadowEl, { lat: shadowLat, lng: shadowLng }, bearing)

      perfMark('flyover:frame:end')
      perfMeasure('flyover:frame', 'flyover:frame:start', 'flyover:frame:end')

      if (t < 1) {
        rafId = requestAnimationFrame(tick)
      } else {
        perfMark('flyover:end')
        perfMeasure('flyover:total', 'flyover:start', 'flyover:end')
        perfMeasure('flyover:animation', 'flyover:first-frame', 'flyover:end')
        flyoverActiveRef.current = false
        try {
          if (mapRef.current) {
            behavior?.enable(PAN_BIT | WHEEL_BIT | PINCH_BIT | DBL_BIT)
          }
        } catch {
          /* behavior may be mid-teardown */
        }

        if (arcPolylineRef.current === progressivePolyline) {
          try {
            arcGroup.removeObject(progressivePolyline)
          } catch {
            /* swallow */
          }
          const fullPolyline = new H.map.Polyline(toLineString(path), {
            data: null,
            style: {
              strokeColor: arcColor,
              lineWidth: 4,
              lineDash: [1, 8],
              lineCap: 'round',
            },
          })
          arcGroup.addObject(fullPolyline)
          arcPolylineRef.current = fullPolyline
        }

        const finalBearing = planeState.bearing
        const planeIconHtml = `<div style="width:36px;height:36px;margin-left:-18px;margin-top:-18px;pointer-events:none"><svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="display:block;transform-origin:center;transform:rotate(${finalBearing}deg)"><path fill="${planeColor}" d="${AIRPLANE_PATH}" /></svg></div>`
        const planeIcon = new H.map.DomIcon(planeIconHtml)
        landedMarker = new H.map.DomMarker(
          { lat: planeState.geo.lat, lng: planeState.geo.lng } as H.geo.IPoint,
          { data: null, icon: planeIcon },
        )
        arcGroup.addObject(landedMarker)

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              planeEl.remove()
            } catch {
              /* may already be removed */
            }
            try {
              shadowEl.remove()
            } catch {
              /* may already be removed */
            }
          })
        })
      }
    }

    let cancelledBeforeStart = false
    const PREFETCH_CAP_MS = 10000
    let capTimer = 0
    const capPromise = new Promise<void>((resolve) => {
      capTimer = window.setTimeout(resolve, PREFETCH_CAP_MS)
    })
    setIsPreparingFlyover(true)
    void Promise.race([
      Promise.all([
        httpPrefetchReadyRef.current ?? Promise.resolve(),
        providerPrefetchReadyRef.current ?? Promise.resolve(),
      ]).then(() => undefined),
      capPromise,
    ]).then(() => {
      window.clearTimeout(capTimer)
      if (cancelledBeforeStart) return
      const flyoverStartZoom = Math.min(closeZoom, midZoom + 3)
      try {
        vm.setLookAtData(
          {
            position: { lat: from.lat, lng: from.lng } as H.geo.IPoint,
            zoom: flyoverStartZoom,
          },
          false,
        )
      } catch {
        /* swallow */
      }
      repositionFromState()
      perfMark('flyover:rAF-start')
      perfMeasure('flyover:settle', 'flyover:start', 'flyover:rAF-start')
      rafId = requestAnimationFrame((now) => {
        setIsPreparingFlyover(false)
        tick(now)
      })
    })

    return () => {
      cancelledBeforeStart = true
      window.clearTimeout(capTimer)
      cancelAnimationFrame(rafId)
      setIsPreparingFlyover(false)
      flyoverActiveRef.current = false
      try {
        if (mapRef.current) {
          behavior?.enable(PAN_BIT | WHEEL_BIT | PINCH_BIT | DBL_BIT)
        }
      } catch {
        /* behavior may be mid-teardown */
      }
      if (landedMarker) {
        try {
          arcGroup.removeObject(landedMarker)
        } catch {
          /* may already be removed */
        }
        landedMarker = null
      }
      try {
        planeEl.remove()
      } catch {
        /* may already be removed */
      }
      try {
        shadowEl.remove()
      } catch {
        /* may already be removed */
      }
    }
  }, [
    mapReady,
    flightArcFlyoverKey,
    resolvedArcFrom?.lat,
    resolvedArcFrom?.lng,
    resolvedArcTo?.lat,
    resolvedArcTo?.lng,
    flightArc?.color,
    arcColorTheme,
  ])

  if (unavailable) {
    return (
      <div
        style={{
          width: mapWidth,
          height: mapHeight,
          marginLeft,
          marginRight,
          borderRadius,
          boxShadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f5f7',
          color: '#5b5b5b',
        }}
      >
        Map unavailable
      </div>
    )
  }

  return (
    <div
      style={{
        width: mapWidth,
        height: mapHeight,
        marginLeft,
        marginRight,
        borderRadius,
        boxShadow,
        overflow: 'hidden',
        position: 'relative',
        textAlign: 'left',
      }}
    >
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', background: '#cfd8dc' }}
      />
      {htmlOverlays && htmlOverlays.length > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            userSelect: 'none',
          }}
        >
          <svg
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'visible',
            }}
          >
            {htmlOverlays.map((o) => {
              const layout = overlayLayouts[o.key]
              if (!layout) return null
              const moved =
                Math.hypot(
                  layout.anchorX - layout.markerX,
                  layout.anchorY - layout.markerY,
                ) > 2
              if (!moved) return null
              return (
                <g key={`${o.key}-leader`}>
                  <line
                    x1={layout.markerX}
                    y1={layout.markerY}
                    x2={layout.anchorX}
                    y2={layout.anchorY}
                    stroke="#00253d"
                    strokeWidth={1.5}
                    strokeDasharray="6 3"
                    opacity={0.55}
                  />
                  <circle
                    cx={layout.markerX}
                    cy={layout.markerY}
                    r={4}
                    fill="#00253d"
                  />
                </g>
              )
            })}
          </svg>
          {htmlOverlays.map((o) => {
            const layout = overlayLayouts[o.key]
            if (!layout) return null
            return (
              <div
                key={o.key}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: `translate(${layout.anchorX}px, ${layout.anchorY}px)`,
                  pointerEvents: 'auto',
                }}
              >
                {o.render({
                  tailSide: layout.tailSide,
                  tailAttach: layout.tailAttach,
                })}
              </div>
            )
          })}
        </div>
      )}
      {isPreparingFlyover && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(11,28,51,0.45)',
            color: '#fff',
            fontFamily: 'system-ui, sans-serif',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: 0.2,
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          Preparing flight…
        </div>
      )}
    </div>
  )
}

export default HereNativeRouteMap
